import { useState, useMemo, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import BackIcon from "@material-ui/icons/ArrowBackRounded";

import SearchTextField from "components/common/SearchTextField";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

import { useBrands } from "features/firebase/brands/BrandsProvider";
import { SuggestionBasedText } from "pages/photo/types";
import { matchesSearchWithSynonyms } from "utils/synonymSearch";
import { useTranslation } from "react-i18next";

const BRAND_ROW_HEIGHT = 56;

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "70vh",
    padding: theme.spacing(2)
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  title: {
    fontWeight: 700,
    fontSize: "1.2rem",
    marginLeft: theme.spacing(1)
  },
  searchField: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.common.white
  },
  sectionLabel: {
    fontSize: "1rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    letterSpacing: 0.5,
    marginBlock: theme.spacing(1.5)
  },
  brandItem: {
    display: "flex",
    alignItems: "center",
    height: BRAND_ROW_HEIGHT,
    boxSizing: "border-box",
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    cursor: "pointer",
    fontSize: "0.95rem",
    color: "#495459"
  },
  listWrapper: {
    flex: 1,
    overflow: "hidden"
  }
}));

type Props = {
  onSelect: (brand: SuggestionBasedText) => void;
  onBack: () => void;
};

export default function BrandPicker({ onSelect, onBack }: Props) {
  const classes = useStyles();
  const brandsJson = useBrands();
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const allBrands = useMemo(() => {
    return Object.entries(brandsJson)
      .map(([id, value]) => ({
        id,
        label: value?.label || id,
        synonyms: value?.synonyms
      }))
      .sort((a, b) => {
        if (a.label === "Unknown") return -1;
        if (b.label === "Unknown") return 1;
        return a.label.localeCompare(b.label);
      });
  }, [brandsJson]);

  const filteredBrands = useMemo(() => {
    if (!search.trim()) return allBrands;
    return allBrands.filter((b) =>
      matchesSearchWithSynonyms(search, b.label, b.synonyms)
    );
  }, [allBrands, search]);

  const handleSelect = useCallback(
    (brand: { id: string; label: string }) => {
      onSelect({ id: brand.id, label: brand.label });
    },
    [onSelect]
  );

  const Row = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const brand = filteredBrands[index];
      return (
        <div
          className={classes.brandItem}
          style={style}
          onClick={() => handleSelect(brand)}
        >
          {brand.label}
        </div>
      );
    },
    [filteredBrands, classes.brandItem, handleSelect]
  );

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <IconButton size="small" onClick={onBack}>
          <BackIcon />
        </IconButton>
        <Typography className={classes.title}>Pick brand</Typography>
      </div>

      <SearchTextField
        value={search}
        onChange={setSearch}
        placeholder={t("record_litter_brand_placeholder")}
        className={classes.searchField}
        fullWidth
      />

      <Typography className={classes.sectionLabel}>
        {search.trim() ? "Results" : "Brands A\u2013Z"}
      </Typography>
      <div className={classes.listWrapper}>
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => (
            <List
              itemCount={filteredBrands.length}
              itemSize={BRAND_ROW_HEIGHT}
              width={width}
              height={height}
            >
              {Row}
            </List>
          )}
        </AutoSizer>
      </div>
    </div>
  );
}
